import React, { useCallback, useRef } from 'react';
import { TimeInput } from './TimeInput';

type TimeSegment = 'hours' | 'minutes';

const selectSegment = (el: HTMLInputElement, segment: TimeSegment) => {
  const start = segment === 'hours' ? 0 : 3;
  el.setSelectionRange(start, start + 2);
};

const getSegment = (el: HTMLInputElement): TimeSegment =>
  (el.selectionStart || 0) < 3 ? 'hours' : 'minutes';

const buildTime = (h: number, m: number) =>
  `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

const splitTime = (v: string): [number, number] => {
  const [hh, mm] = v.split(':');
  return [parseInt(hh, 10), parseInt(mm, 10)];
};

export interface SegmentedTimeInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
  onFocus?: (currentValue: string) => string | undefined;
}

export const SegmentedTimeInput: React.FC<SegmentedTimeInputProps> = ({
  id,
  value,
  onChange,
  ariaLabel,
  placeholder = 'HH:MM',
  onFocus,
}) => {
  const digitBuffer = useRef('');

  const handleFocus = useCallback(() => {
    digitBuffer.current = '';
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el) return;

    if (!value) {
      const autoFilled = onFocus?.(value);
      if (autoFilled !== undefined) {
        onChange(autoFilled);
      } else {
        onChange('00:00');
      }
      setTimeout(() => selectSegment(el, 'hours'), 0);
      return;
    }

    onFocus?.(value);
    setTimeout(() => selectSegment(el, getSegment(el)), 0);
  }, [id, value, onChange, onFocus]);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    const el = e.currentTarget;
    if (value) {
      selectSegment(el, getSegment(el));
      digitBuffer.current = '';
    }
  }, [value]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    if (!value) return;

    const segment = getSegment(el);
    const [h, m] = splitTime(value);

    // Tab: move between segments or let browser handle
    if (e.key === 'Tab') {
      if (!e.shiftKey && segment === 'hours') {
        e.preventDefault();
        digitBuffer.current = '';
        selectSegment(el, 'minutes');
        return;
      }
      if (e.shiftKey && segment === 'minutes') {
        e.preventDefault();
        digitBuffer.current = '';
        selectSegment(el, 'hours');
        return;
      }
      digitBuffer.current = '';
      return;
    }

    // Arrow Left / Right: switch segment
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      digitBuffer.current = '';
      selectSegment(el, 'hours');
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      digitBuffer.current = '';
      selectSegment(el, 'minutes');
      return;
    }

    // Arrow Up / Down: increment/decrement
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      digitBuffer.current = '';
      const delta = e.key === 'ArrowUp' ? 1 : -1;
      const newH = segment === 'hours' ? (h + delta + 24) % 24 : h;
      const newM = segment === 'minutes' ? (m + delta + 60) % 60 : m;
      onChange(buildTime(newH, newM));
      setTimeout(() => selectSegment(el, segment), 0);
      return;
    }

    // Backspace / Delete: zero out segment
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      digitBuffer.current = '';
      onChange(segment === 'hours' ? buildTime(0, m) : buildTime(h, 0));
      setTimeout(() => selectSegment(el, segment), 0);
      return;
    }

    // Digit: type into active segment
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const digit = e.key;
      const maxFirst = segment === 'hours' ? 2 : 5;

      if (digitBuffer.current === '') {
        const d = parseInt(digit, 10);
        if (d > maxFirst) {
          const val = parseInt(`0${digit}`, 10);
          if (segment === 'hours') {
            onChange(buildTime(val, m));
          } else {
            onChange(buildTime(h, val));
          }
          digitBuffer.current = '';
          setTimeout(() => selectSegment(el, 'minutes'), 0);
        } else {
          digitBuffer.current = digit;
          if (segment === 'hours') {
            onChange(buildTime(parseInt(`${digit}0`, 10), m));
          } else {
            onChange(buildTime(h, parseInt(`${digit}0`, 10)));
          }
          setTimeout(() => selectSegment(el, segment), 0);
        }
      } else {
        const twoDigit = parseInt(digitBuffer.current + digit, 10);
        digitBuffer.current = '';
        if (segment === 'hours') {
          onChange(buildTime(Math.min(twoDigit, 23), m));
        } else {
          onChange(buildTime(h, Math.min(twoDigit, 59)));
        }
        setTimeout(() => selectSegment(el, 'minutes'), 0);
      }
      return;
    }

    // Block all other printable keys, allow meta/ctrl shortcuts
    if (!e.metaKey && !e.ctrlKey && e.key.length === 1) {
      e.preventDefault();
    }
  }, [value, onChange]);

  return (
    <TimeInput
      id={id}
      value={value}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      onFocus={handleFocus}
      onMouseUp={handleMouseUp}
      onKeyDown={handleKeyDown}
    />
  );
};
